import type { LobbyState, ServerMessage } from '@nanocars/shared';
import { NetClient } from '../net/NetClient';
import { Input } from '../input/Input';

const NAME_STORAGE_KEY = 'nanocars.name';
const DEFAULT_NAME = 'Driver';

export type RaceStartMessage = Extract<ServerMessage, { t: 'raceStart' }>;

function readName(): string {
  try {
    if (typeof localStorage === 'undefined') return DEFAULT_NAME;
    const stored = localStorage.getItem(NAME_STORAGE_KEY);
    return stored && stored.trim().length > 0 ? stored : DEFAULT_NAME;
  } catch {
    return DEFAULT_NAME;
  }
}

function writeName(value: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(NAME_STORAGE_KEY, value);
  } catch {
    // Storage can throw (private browsing, quota); losing persistence isn't fatal.
  }
}

// Cross-scene state: the network connection, the input source, and whatever
// the server has told us about the lobby/race so far. Scenes read and write
// this directly instead of threading props down a scene tree - there is
// only ever one of each at a time. `session` below is the app-wide instance;
// the class is exported too so tests can construct isolated ones.
export class Session {
  readonly net = new NetClient();
  readonly input = new Input();
  playerId: number | null = null;
  lobby: LobbyState | null = null;
  lastRaceStart: RaceStartMessage | null = null;
  private _name: string = readName();

  get name(): string {
    return this._name;
  }

  set name(value: string) {
    const trimmed = value.trim();
    this._name = trimmed.length > 0 ? trimmed : DEFAULT_NAME;
    writeName(this._name);
  }
}

export const session = new Session();

// Dev server always runs on 3490 (see packages/server); production is
// expected to sit behind the same host under /ws. `?ws=` lets a preview
// build (e.g. a production `vite preview` on localhost, whose default
// production URL can't reach a local server) point at an explicit socket
// URL for manual/screenshot verification.
export function wsUrl(): string {
  const override = new URLSearchParams(location.search).get('ws');
  if (override) return override;
  if (import.meta.env.DEV) return `ws://${location.hostname}:3490`;
  return `wss://${location.host}/ws`;
}
